import { useParams } from 'react-router-dom'
import type { Project } from '@/store/projects/projectsSlice'
import { useWorkspace, type WorkspaceMember } from '../../../../contexts/WorkspaceContext'
import { useAppSelector } from '../../../../store/hooks'
import { selectProjectMemberIds } from '../../../../store/projects/projectsSelectors'
import { selectMembersInitialized } from '@/store/workspace/workspaceSelectors'
import { TextH3 } from '../../../ui/Text'
import AvatarStack from '@/components/ui/AvatarStack/AvatarStack'
import AvatarStackSkeleton from '@/components/ui/AvatarStack/AvatarStackSkeleton'
import { useRelativeTime } from '../../../../hooks/useRelativeTime'
import Link from '../../../app/Link/Link'
import css from './WorkspaceProjectsItem.module.css'

interface WorkspaceProjectsItemProps {
    project: Project
}

const WorkspaceProjectsItem = ({ project }: WorkspaceProjectsItemProps) => {
    const { workspace } = useParams<{ workspace: string }>()
    const formatRelativeTime = useRelativeTime()
    const { getMember } = useWorkspace()

    const projectMemberIds = useAppSelector(state => selectProjectMemberIds(state, project.id))
    const membersInitialized = useAppSelector(selectMembersInitialized)

    const allMembers = Array.from(projectMemberIds)
        .map(userId => getMember(userId))
        .filter((m): m is WorkspaceMember => m !== undefined)

    // Members load separately from projects, so until that first snapshot lands the
    // project's member ids resolve to nothing — show avatar skeletons instead of an
    // empty slot that pops in a frame later (SCD-888).
    const memberCount = Array.from(projectMemberIds).length
    const showSkeleton = !membersInitialized && memberCount > 0

    const hasName = project.name.trim().length > 0

    const truncatedName = project.name.length > 43
        ? project.name.slice(0, 43) + '...'
        : project.name

    const titleClassName = hasName ? css.title : `${css.title} ${css.untitled}`
    const updatedAtText = project.updatedAt ? formatRelativeTime(project.updatedAt) : ''

    return (
        <Link href={`/${workspace}/projects/${project.id}`} className={css.container} testId="project-item">
            <div className={titleClassName}>
                <TextH3>{hasName ? truncatedName : 'Untitled'}</TextH3>
            </div>
            <div className={css.bottom}>
                <div className={css.members}>
                    {showSkeleton && <AvatarStackSkeleton count={memberCount} />}
                    {!showSkeleton && allMembers.length > 0 && <AvatarStack
                        members={allMembers.map((m: WorkspaceMember) => ({
                            id: m.userId,
                            username: m.username,
                            color: m.color,
                            avatarImage: m.avatarImage
                        }))}
                    />}
                </div>
                <div className={css.updated}>{updatedAtText}</div>
            </div>
        </Link>
    )
}

export default WorkspaceProjectsItem
